/* eslint-disable react/jsx-max-depth */
/* eslint-disable max-lines-per-function, no-nested-ternary, complexity, max-lines */

import { Content, Icon, InputText, Modal, TextArea } from "@bluframe/blublocks"
import Button from "components/buttons/Button"
import Checkbox from "components/Cards/Card/Actions/Checkbox"
import ConfirmModal from "components/ConfirmModal"
import {
  FieldLabel,
  FieldLabelAndValue,
  FieldRow,
  FieldValue
} from "components/Fields/styled"
import ImageSelectorAndCarousel from "components/ImageSelectorAndCarousel"
import Select from "components/inputs/Select"
import {
  Actions,
  Form,
  InputAndButton,
  Main,
  NameAndInfo,
  Top
} from "components/List/ItemView/styled"
import Typography from "components/Typography"

import { ComponentProps } from "."

const Item = ({
  actionButtonLabel,
  actionButtonOnClick,
  additionalInstructionsRef,
  authorSelectValue,
  areActionsDisabled,
  contentAuthors,
  hasLinkedHomeVideo,
  isAdd,
  isAdmin,
  isEditMode,
  isContentAuthorsLoading,
  isEditor,
  isGenerateLoading,
  isModalOpen,
  isPublishLoading,
  isUpdateLoading,
  item,
  modalContent,
  nameRef,
  onAuthorChange,
  onGenerateSlug,
  onModalClose,
  onModalConfirmOpen,
  onModalDeleteConfirm,
  onModalGenerateConfirm,
  onModalPublishToggleConfirm,
  onToggleCascadeDelete,
  onSetSelectedImages,
  onSubmit,
  outlinedButtonLabel,
  outlinedButtonOnClick,
  selectedImages,
  slugRef,
  shouldCascadeDelete,
  subheadingRef,
  textRef
}: ComponentProps) => (
  <Form onSubmit={onSubmit}>
    <Top>
      <Main>
        <NameAndInfo $isEditMode={isEditMode}>
          {isEditMode ? (
            <>
              <InputText
                defaultValue={item.name}
                inputId="name"
                label="Name"
                name="name"
                ref={nameRef}
                required
              />

              <TextArea
                defaultValue={item.subheading}
                inputId="subheading"
                label="Subheading"
                name="subheading"
                ref={subheadingRef}
                rows={3}
              />

              <FieldRow $items={isAdmin || isEditor ? 2 : 1}>
                <InputAndButton>
                  <InputText
                    defaultValue={item.slug}
                    inputId="slug"
                    label="Slug"
                    name="slug"
                    ref={slugRef}
                    required
                  />

                  <Button
                    icon={<Icon group="fa6" name="FaRotateLeft" />}
                    iconOnly
                    label="Generate Slug"
                    onClick={onGenerateSlug}
                    outlined
                    transparent
                  />
                </InputAndButton>

                {isAdmin || isEditor ? (
                  <Select
                    label="Author"
                    loading={isContentAuthorsLoading}
                    name="news-author"
                    onChange={onAuthorChange}
                    options={
                      contentAuthors?.map((author) => ({
                        label: author.name,
                        value: Number(author.id)
                      })) ?? []
                    }
                    placeholder="Select an author"
                    value={authorSelectValue ?? undefined}
                  />
                ) : null}
              </FieldRow>
            </>
          ) : (
            <>
              <Typography.H1>{item.name}</Typography.H1>

              <Typography.SubtitleH2>{item.subheading}</Typography.SubtitleH2>

              <FieldRow $items={2}>
                <FieldLabelAndValue>
                  <FieldLabel>{"Slug"}</FieldLabel>

                  <FieldValue>{item.slug}</FieldValue>
                </FieldLabelAndValue>

                <FieldLabelAndValue>
                  <FieldLabel>{"Author"}</FieldLabel>

                  <FieldValue>{item.author?.name ?? "Not assigned"}</FieldValue>
                </FieldLabelAndValue>
              </FieldRow>
            </>
          )}
        </NameAndInfo>

        <ImageSelectorAndCarousel
          images={item?.images ?? undefined}
          isEditMode={isEditMode}
          onSetSelectedImages={onSetSelectedImages}
          selectedImages={selectedImages}
        />

        {isEditMode ? (
          <TextArea
            defaultValue={item.text}
            inputId="text"
            label="Text"
            name="text"
            ref={textRef}
          />
        ) : item.text ? (
          <FieldLabelAndValue>
            <FieldLabel>{"Text"}</FieldLabel>

            <Content content={item.text} />
          </FieldLabelAndValue>
        ) : null}
      </Main>

      <Actions aria-label="Actions" role="region">
        {isAdmin || isEditor ? (
          <Button
            disabled={areActionsDisabled}
            label={outlinedButtonLabel}
            onClick={outlinedButtonOnClick}
            outlined
          />
        ) : null}

        {isAdmin || isEditor ? (
          <Button
            disabled={areActionsDisabled}
            label={actionButtonLabel}
            loading={isEditMode ? isUpdateLoading : false}
            onClick={actionButtonOnClick}
            type={isEditMode ? "submit" : "button"}
          />
        ) : null}

        {isAdmin || isEditor ? (
          <Button
            disabled={isEditMode || areActionsDisabled}
            label="Generate"
            loading={isGenerateLoading}
            onClick={onModalConfirmOpen("generateConfirm")}
            outlined
            secondary
          />
        ) : null}

        {isAdd ? null : isAdmin || isEditor ? (
          <Button
            disabled={isEditMode || areActionsDisabled}
            label={item.isPublished ? "Unpublish" : "Publish"}
            loading={isPublishLoading}
            onClick={onModalConfirmOpen("publishConfirm")}
            outlined={item.isPublished}
            secondary
          />
        ) : null}

        {isAdmin || isEditor ? (
          <TextArea
            inputId="additionalInstructions"
            label="Additional Instructions"
            name="additionalInstructions"
            ref={additionalInstructionsRef}
            rows={3}
          />
        ) : null}

        {item?.homeVideo ? (
          <FieldLabelAndValue>
            <FieldLabel>{"Home Video"}</FieldLabel>

            <FieldValue>
              <Typography.Link
                to={`/home-videos/home-videos/${item?.homeVideo?.id}`}
              >
                {item?.homeVideo.name}
              </Typography.Link>
            </FieldValue>
          </FieldLabelAndValue>
        ) : null}

        <FieldRow>
          <FieldLabelAndValue>
            <FieldLabel>{"Created At"}</FieldLabel>

            <FieldValue>
              {item?.createdAt
                ? `${new Date(item.createdAt).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "long",
                    year: "numeric"
                  })}, ${new Date(item.createdAt).toLocaleTimeString("en-GB", {
                    hour: "2-digit",
                    minute: "2-digit"
                  })}`
                : undefined}
            </FieldValue>
          </FieldLabelAndValue>

          <FieldLabelAndValue>
            <FieldLabel>{"Updated At"}</FieldLabel>

            <FieldValue>
              {item?.updatedAt
                ? `${new Date(item.updatedAt).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "long",
                    year: "numeric"
                  })}, ${new Date(item.updatedAt).toLocaleTimeString("en-GB", {
                    hour: "2-digit",
                    minute: "2-digit"
                  })}`
                : undefined}
            </FieldValue>
          </FieldLabelAndValue>

          {item?.publishedAt ? (
            <FieldLabelAndValue>
              <FieldLabel>{"Published At"}</FieldLabel>

              <FieldValue>
                {item?.publishedAt
                  ? `${new Date(item.publishedAt).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "long",
                      year: "numeric"
                    })}, ${new Date(item.publishedAt).toLocaleTimeString(
                      "en-GB",
                      {
                        hour: "2-digit",
                        minute: "2-digit"
                      }
                    )}`
                  : undefined}
              </FieldValue>
            </FieldLabelAndValue>
          ) : null}
        </FieldRow>
      </Actions>
    </Top>

    <Modal isOpen={isModalOpen} onClose={onModalClose}>
      {modalContent === "deleteConfirm" ? (
        <ConfirmModal
          message={
            <>
              <Typography.H5>
                {`Are you sure you want to delete ${item.name}?`}
              </Typography.H5>

              {hasLinkedHomeVideo ? (
                <Checkbox
                  checked={shouldCascadeDelete}
                  disabled={false}
                  inputId="cascade-delete-home-video"
                  label="Also delete linked Home Video"
                  name="cascadeDeleteHomeVideo"
                  onChange={onToggleCascadeDelete}
                />
              ) : null}
            </>
          }
          onClose={onModalClose}
          onConfirm={onModalDeleteConfirm}
        />
      ) : null}

      {modalContent === "generateConfirm" ? (
        <ConfirmModal
          message={`Are you sure you want to generate ${item.name}?`}
          onClose={onModalClose}
          onConfirm={onModalGenerateConfirm}
        />
      ) : null}

      {modalContent === "publishConfirm" ? (
        <ConfirmModal
          message={`Are you sure you want to ${
            item.isPublished ? "unpublish" : "publish"
          } ${item.name}?`}
          onClose={onModalClose}
          onConfirm={onModalPublishToggleConfirm}
        />
      ) : null}
    </Modal>
  </Form>
)

export default Item
